import type { Branding, TextSize } from "./types";

// Applies a company's colors and font and the person's text size to the whole page, as the
// mockup's applyBrand() does: the main and accent colors become the CSS colors (link text is the
// accent, darkened), the font is loaded from Google Fonts (or the company's uploaded font file,
// brief 1A-7), and the text size zooms the page.

export const TEXT_SCALE: Record<TextSize, number> = { small: 0.92, standard: 1, large: 1.12 };
export const TEXT_LABEL: Record<TextSize, string> = {
  small: "Small",
  standard: "Standard",
  large: "Large",
};

/** The mockup's shade(): darker (f < 0) or lighter (f > 0). */
export function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const m = (c: number) =>
    Math.max(0, Math.min(255, Math.round(f < 0 ? c * (1 + f) : c + (255 - c) * f)));
  return `#${[n >> 16, (n >> 8) & 255, n & 255].map((c) => m(c).toString(16).padStart(2, "0")).join("")}`;
}

const loaded = new Set(["Inter"]);
/** Uploaded fonts already added to the page, by address. */
const uploaded = new Map<string, string>();

/** Adds an uploaded font file to the page once and answers the family name to use. */
function uploadedFamily(url: string): string {
  let family = uploaded.get(url);
  if (!family) {
    family = `TenantFont${uploaded.size + 1}`;
    uploaded.set(url, family);
    const face = new FontFace(family, `url(${url})`);
    document.fonts.add(face);
    // A font file that doesn't load leaves the fallback fonts showing.
    face.load().catch(() => undefined);
  }
  return family;
}

export function applyTextSize(size: TextSize) {
  document.documentElement.style.zoom = String(TEXT_SCALE[size]);
  document.documentElement.dataset.textSize = size;
}

export function applyBranding(b: Branding, size: TextSize) {
  applyTextSize(size);
  let st = document.getElementById("brandcss") as HTMLStyleElement | null;
  if (!st) {
    st = document.createElement("style");
    st.id = "brandcss";
    document.head.append(st);
  }
  const vars = `--primary:${b.primaryColor};--accent:${b.accentColor};--accent-text:${shade(b.accentColor, -0.3)}`;
  // Dark mode keeps its own light-on-dark colors, as in the mockup.
  const family = b.fontFile ? uploadedFamily(b.fontFile.url) : b.font;
  st.textContent =
    `html:root{--font:"${family}",system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif}` +
    `@media not (prefers-color-scheme:dark){html:root:not([data-theme="dark"]){${vars}}}` +
    `html:root[data-theme="light"]{${vars}}`;
  if (!b.fontFile && !loaded.has(b.font)) {
    loaded.add(b.font);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(b.font)}:wght@400;500;600;700&display=swap`;
    document.head.append(link);
  }
  document.title = b.appName;
  const name = document.getElementById("app-name");
  if (name) name.textContent = b.appName;
}
